import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { inventoryService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import type { MaintenanceRecord } from "../types/database";

interface MaintenanceRecordFormProps {
  assetId: string;
  record?: MaintenanceRecord | null;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function MaintenanceRecordForm({
  assetId,
  record,
  onSuccess,
  onCancel,
}: MaintenanceRecordFormProps) {
  const { user, currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    date: record?.date ? new Date(record.date).toISOString().split("T")[0] : "",
    type: record?.type || "inspection",
    notes: record?.notes || "",
    performed_by: record?.performed_by || "",
    cost: record?.cost?.toString() || "",
  });

  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value === "" || !isNaN(Number(value))) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const addMaintenanceRecordMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("User not authenticated");
      if (!currentOrganization) throw new Error("No organization selected");

      const recordData = {
        asset_id: assetId,
        date: formData.date
          ? new Date(formData.date).toISOString()
          : new Date().toISOString(),
        type: formData.type as "repair" | "inspection" | "cleaning",
        notes: formData.notes || null,
        performed_by: formData.performed_by || null,
        cost: formData.cost ? parseFloat(formData.cost) : null,
        created_by: user.id,
        organization_id: currentOrganization.id,
      };

      return await inventoryService.addMaintenanceRecord(recordData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["asset", assetId] });
      if (onSuccess) onSuccess();
    },
  });

  const updateMaintenanceRecordMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("User not authenticated");
      if (!currentOrganization) throw new Error("No organization selected");
      if (!record) throw new Error("No record to update");

      const updates = {
        date: formData.date
          ? new Date(formData.date).toISOString()
          : new Date().toISOString(),
        type: formData.type as "repair" | "inspection" | "cleaning",
        notes: formData.notes || null,
        performed_by: formData.performed_by || null,
        cost: formData.cost ? parseFloat(formData.cost) : null,
      };

      return await inventoryService.updateMaintenanceRecord(
        record.id,
        updates,
        currentOrganization.id
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["asset", assetId] });
      if (onSuccess) onSuccess();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.date) {
      setError("Date is required");
      return;
    }

    if (!formData.type) {
      setError("Type is required");
      return;
    }

    if (record) {
      updateMaintenanceRecordMutation.mutate();
    } else {
      addMaintenanceRecordMutation.mutate();
    }
  };

  const isSubmitting =
    addMaintenanceRecordMutation.isPending ||
    updateMaintenanceRecordMutation.isPending;

  return (
    <div className="bg-white rounded-lg">
      <h2 className="text-xl font-semibold mb-4">
        {record ? "Edit Maintenance Record" : "Add Maintenance Record"}
      </h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type <span className="text-red-500">*</span>
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              required
            >
              <option value="inspection">Inspection</option>
              <option value="repair">Repair</option>
              <option value="cleaning">Cleaning</option>
            </select>
          </div>

          {/* Performed By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Performed By
            </label>
            <input
              type="text"
              name="performed_by"
              value={formData.performed_by}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="e.g. John Smith, External Contractor"
            />
          </div>

          {/* Cost */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cost
            </label>
            <input
              type="text"
              name="cost"
              value={formData.cost}
              onChange={handleNumberChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="Describe the maintenance work performed..."
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? record
                ? "Updating..."
                : "Adding..."
              : record
              ? "Update Record"
              : "Add Record"}
          </button>
        </div>
      </form>
    </div>
  );
}
